import React, { useState } from 'react';
import { WebSocketTest } from '../components/WebSocketTest';
import { useTheme } from '../contexts/ThemeContext';

const WebSocketTestPage: React.FC = () => {
  const { darkMode } = useTheme();
  const [customEndpoint, setCustomEndpoint] = useState<string>('');
  const [showCustomTest, setShowCustomTest] = useState<boolean>(false);

  return (
    <div className="p-4">
      <h1 className="text-2xl font-bold mb-4">WebSocket Connection Test</h1>
      
      <div className="mb-8">
        <h2 className="text-xl font-semibold mb-2">Default Test Endpoint</h2>
        <p className="mb-4 text-gray-600 dark:text-gray-400">
          This tests the connection to the default test endpoint at <code>/ws/test</code>
        </p>
        <div className="border rounded-lg p-4">
          <WebSocketTest />
        </div>
      </div>
      
      <div className="mb-8">
        <h2 className="text-xl font-semibold mb-2">Custom Endpoint Test</h2>
        <div className="flex items-center mb-4">
          <input
            type="text"
            value={customEndpoint}
            onChange={(e) => setCustomEndpoint(e.target.value)}
            placeholder="Enter endpoint path (e.g., /ws/workflows/test-id)"
            className={`flex-1 px-3 py-2 border rounded-md mr-2 ${
              darkMode 
                ? 'bg-gray-700 border-gray-600 text-white placeholder-gray-400' 
                : 'bg-white border-gray-300 text-gray-900 placeholder-gray-500'
            }`}
          />
          <button
            onClick={() => setShowCustomTest(true)}
            className="px-4 py-2 bg-blue-600 text-white rounded hover:bg-blue-700"
          >
            Test Connection
          </button>
        </div>
        
        {showCustomTest && customEndpoint && (
          <div className="border rounded-lg p-4">
            <h3 className="font-medium mb-2">Testing connection to: {customEndpoint}</h3>
            <CustomWebSocketTest endpoint={customEndpoint} />
          </div>
        )}
      </div>
      
      <div className="bg-gray-100 dark:bg-gray-800 p-4 rounded-lg">
        <h2 className="text-xl font-semibold mb-2">Troubleshooting Tips</h2>
        <ul className="list-disc pl-5 space-y-2">
          <li>Make sure the backend server is running</li>
          <li>Check that the WebSocket endpoint exists on the backend</li>
          <li>Verify the proxy configuration in vite.config.ts</li>
          <li>Check browser console for detailed error messages</li>
          <li>Ensure the URL format is correct (ws:// or wss://)</li>
        </ul>
      </div>
    </div>
  );
};

// Custom WebSocket test component for testing arbitrary endpoints
const CustomWebSocketTest: React.FC<{ endpoint: string }> = ({ endpoint }) => {
  const [status, setStatus] = useState('Disconnected');
  const [messages, setMessages] = useState<string[]>([]);
  const [error, setError] = useState<string | null>(null);

  React.useEffect(() => {
    // Create a test WebSocket connection
    const protocol = window.location.protocol === 'https:' ? 'wss:' : 'ws:';
    const host = window.location.host;
    
    // Ensure endpoint starts with a slash
    const formattedEndpoint = endpoint.startsWith('/') ? endpoint : `/${endpoint}`;
    const wsUrl = `${protocol}//${host}${formattedEndpoint}`;
    
    console.log(`[CUSTOM-WEBSOCKET-TEST] Attempting to connect to: ${wsUrl}`);
    
    let ws: WebSocket;
    try {
      ws = new WebSocket(wsUrl);
      
      ws.onopen = () => {
        setStatus('Connected');
        setMessages(prev => [...prev, 'Connection established']);
        setError(null);
        console.log('[CUSTOM-WEBSOCKET-TEST] Connection established');
        
        // Send a test message
        ws.send(JSON.stringify({ type: 'ping', message: 'Hello from test client' }));
      };
      
      ws.onmessage = (event) => {
        console.log('[CUSTOM-WEBSOCKET-TEST] Message received:', event.data);
        setMessages(prev => [...prev, `Received: ${event.data}`]);
      };
      
      ws.onerror = (error) => {
        console.error('[CUSTOM-WEBSOCKET-TEST] Error:', error);
        setStatus('Error');
        setError('Connection error occurred. Check console for details.');
        setMessages(prev => [...prev, `Error occurred`]);
      };
      
      ws.onclose = (event) => {
        console.log('[CUSTOM-WEBSOCKET-TEST] Connection closed, code:', event.code);
        setStatus('Closed');
        setMessages(prev => [...prev, `Connection closed (code: ${event.code})`]);
        
        if (event.code === 1006) {
          setError('Abnormal closure (code 1006). The connection was closed unexpectedly.');
        }
      };
      
      return () => {
        console.log('[CUSTOM-WEBSOCKET-TEST] Cleaning up connection');
        ws.close();
      };
    } catch (err) {
      console.error('[CUSTOM-WEBSOCKET-TEST] Failed to create WebSocket:', err);
      setStatus('Error');
      setError(`Failed to create WebSocket: ${err.message}`);
      return () => {};
    }
  }, [endpoint]);

  return (
    <div>
      <div className="mb-2">
        Status: <span className={
          status === 'Connected' ? 'text-green-500' : 
          status === 'Error' ? 'text-red-500' : 
          'text-yellow-500'
        }>{status}</span>
      </div>
      
      {error && (
        <div className="mb-2 p-2 bg-red-100 border border-red-300 text-red-800 rounded">
          {error}
        </div>
      )}
      
      <div className="mt-2">
        <h4 className="font-medium">Messages:</h4>
        {messages.length === 0 ? (
          <p className="text-gray-500">No messages yet</p>
        ) : (
          <ul className="list-disc pl-5 mt-1">
            {messages.map((msg, i) => (
              <li key={i} className="text-sm">{msg}</li>
            ))}
          </ul>
        )}
      </div>
    </div>
  );
};

export default WebSocketTestPage;
